import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './CollectionActionsSheet.styled';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { NameForm } from '@/features/BookmarkCollections/components/NameForm';
import { BookmarkCollection } from '@/features/BookmarkCollections/api';
import { ConfirmDeleteModal } from '@/features/BookmarkCollections/components/ConfirmDeleteModal';

interface CollectionActionsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  hasSharing?: boolean;
  collection: BookmarkCollection | null;
}

export const CollectionActionsSheet = ({
  isOpen,
  onClose,
  onDelete,
  collection,
  hasSharing = false,
}: CollectionActionsSheetProps) => {
  const { t } = useTranslation();

  const [isEditing, setIsEditing] = useState(false);
  const [isDestroyConfirmOpen, setIsDestroyConfirmOpen] = useState(false);

  const destroyCollection = () => {
    if (!collection) return;

    onDelete();
    setIsDestroyConfirmOpen(false);
  };

  const handleDeleteClick = () => {
    onClose();
    setIsDestroyConfirmOpen(true);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => setIsEditing(false), MODAL_ANIMATION_DURATION);
  };

  const handleManualClose = () => {
    if (isEditing) {
      setIsEditing(false);
    } else {
      onClose();
    }
  };

  const handleShareCollection = () => {
    if (navigator.share && collection?.id) {
      navigator.share({
        title: collection?.name,
        url: `${window.location.origin}/collections/${collection.id}`,
      });
    }
  };

  return (
    <>
      <BottomSheet
        isTitleCentered
        isOpen={isOpen}
        close={handleClose}
        onCloseClick={handleManualClose}
        title={
          isEditing
            ? t('bookmark_collections.edit_collection_dropdown')
            : t('bookmark_collections.edit_collection_title')
        }
      >
        {isEditing ? (
          <NameForm
            mode="edit"
            onSuccess={handleClose}
            collection={collection}
          />
        ) : (
          <S.Content>
            <S.Item
              onClick={() => setIsEditing(true)}
              data-testid="collections-sheet-item-edit"
            >
              <Typography.Body $variant="1">
                {t('bookmark_collections.edit_collection_dropdown')}
              </Typography.Body>
            </S.Item>
            <S.Item
              onClick={handleDeleteClick}
              data-testid="collections-sheet-item-delete"
            >
              <Typography.Body $variant="1">
                {t('bookmark_collections.delete_collection_dropdown')}
              </Typography.Body>
            </S.Item>
            {hasSharing && (
              <S.Item onClick={handleShareCollection}>
                <Typography.Body $variant="1">
                  {t('bookmark_collections.share_collection_dropdown')}
                </Typography.Body>
              </S.Item>
            )}
          </S.Content>
        )}
      </BottomSheet>

      <ConfirmDeleteModal
        onSubmit={destroyCollection}
        isOpen={isDestroyConfirmOpen}
        onClose={() => setIsDestroyConfirmOpen(false)}
      />
    </>
  );
};
